import { useState, useMemo } from 'react';
import { X, MinusCircle, ChevronsRight, ChevronsLeft, Eraser } from 'lucide-react';
import { STATUS_STYLES } from './utils';

/**
 * One full company section in the board:
 *   ┌─ Company header
 *   │  ├─ Depot header (optional)
 *   │  │  ├─ Route row × N → 7 day cells
 *   │  └─ Depot header
 *   └─ ...
 *
 * Note: per-company "Yeni Depo" inline button removed (Apr 30, 2026) — depot
 * yönetimi tek noktadan toolbar'daki "Depo & Rota" diyaloğundan yapılır;
 * tablo içinde duplicate CTA kalabalık yaratıyordu.
 */
export function FirmGroup({ company, dates, firstColWidth, lockedDates, assignByCell, onDropCell, onDragOverCell, onDragLeaveCell, dwellingCell, dwellMs, onRemoveAssignment, onContextMenu, onAssignmentDragStart, draggingAssignmentId, onCloseEmptyCell, onRenameDepot, onClearRoute, isSuperAdmin, focusedEmpId, collapsed = false, onToggleCollapsed, liveProgressByCode, liveProgressTodayISO, onLiveChipClick, onTapEmptyCell, pickModeActive }) {
  // Aksiyon ikonu için minimal hesap: müşteri grubunda toplam rota & atama
  // sayısı — kapalı görünümde başlığın yanında özet rozet olarak gösterilir
  // ki operatör hangi müşterinin daha yoğun olduğunu açmadan görsün.
  const summary = (() => {
    let routeCount = 0;
    let assignCount = 0;
    for (const d of company.depots || []) {
      for (const r of d.routes || []) {
        routeCount += 1;
        for (const date of dates) {
          const a = assignByCell?.get(`${r.id}|${date}`);
          if (a && a.status !== 'empty' && a.status !== 'cancelled') assignCount += 1;
        }
      }
    }
    return { routeCount, assignCount };
  })();

  return (
    <>
      <tr className="bg-slate-50 dark:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors">
        <td
          colSpan={8}
          className={`px-3 py-1.5 text-xs font-bold text-slate-700 dark:text-slate-200 sticky left-0 ${onToggleCollapsed ? 'cursor-pointer select-none' : ''}`}
          onClick={() => onToggleCollapsed?.()}
          data-testid={`planning-firm-row-${company.company_id}`}
          title={onToggleCollapsed ? (collapsed ? 'Tıkla: depo+rota satırlarını aç' : 'Tıkla: depo+rota satırlarını kapat') : undefined}
        >
          <div className="flex items-center gap-2">
            {onToggleCollapsed && (
              <span
                className={`inline-block transition-transform ${collapsed ? '' : 'rotate-90'}`}
                aria-hidden="true"
              >▶</span>
            )}
            <span>{company.company_name}</span>
            {collapsed && (
              <span className="ml-auto inline-flex items-center gap-2 text-[10px] font-semibold text-slate-500">
                <span className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">{summary.routeCount} rota</span>
                <span className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">{summary.assignCount} atama</span>
              </span>
            )}
          </div>
        </td>
      </tr>
      {!collapsed && company.depots.map((d) => (
        <DepotRows
          key={d.depot_id || 'none'}
          depot={d}
          dates={dates}
          firstColWidth={firstColWidth}
          lockedDates={lockedDates}
          assignByCell={assignByCell}
          onDropCell={onDropCell}
          onDragOverCell={onDragOverCell}
          onDragLeaveCell={onDragLeaveCell}
          dwellingCell={dwellingCell}
          dwellMs={dwellMs}
          onRemoveAssignment={onRemoveAssignment}
          onContextMenu={onContextMenu}
          onAssignmentDragStart={onAssignmentDragStart}
          draggingAssignmentId={draggingAssignmentId}
          onCloseEmptyCell={onCloseEmptyCell}
          onRenameDepot={onRenameDepot}
          onClearRoute={onClearRoute}
          isSuperAdmin={isSuperAdmin}
          focusedEmpId={focusedEmpId}
          liveProgressByCode={liveProgressByCode}
          liveProgressTodayISO={liveProgressTodayISO}
          onLiveChipClick={onLiveChipClick}
          onTapEmptyCell={onTapEmptyCell}
          pickModeActive={pickModeActive}
        />
      ))}
    </>
  );
}

function DepotRows({ depot, dates, firstColWidth, lockedDates, assignByCell, onDropCell, onDragOverCell, onDragLeaveCell, dwellingCell, dwellMs, onRemoveAssignment, onContextMenu, onAssignmentDragStart, draggingAssignmentId, onCloseEmptyCell, onRenameDepot, onClearRoute, isSuperAdmin, focusedEmpId, liveProgressByCode, liveProgressTodayISO, onLiveChipClick, onTapEmptyCell, pickModeActive }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(depot.depot_name || '');
  // Şub 2026 — Depo başlığındaki >>/<< toggle: rotalarda hiç atama yoksa
  // (haftanın hiçbir gününde) o rotayı gizler — operatör yoğun tahtada
  // ekran alanı kazanır. localStorage'da depot_id ile persist edilir
  // (session ötesinde de kalır). Toggle yalnızca depo başlığı varsa gösterilir.
  const storageKey = depot.depot_id ? `planning.depot.hideEmpty.${depot.depot_id}` : null;
  const [hideEmpty, setHideEmpty] = useState(() => {
    if (!storageKey) return false;
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  const toggleHideEmpty = () => {
    setHideEmpty((prev) => {
      const next = !prev;
      if (storageKey) {
        try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* ignore */ }
      }
      return next;
    });
  };

  // Görünür rotalar: hideEmpty aktifse haftanın en az bir gününde geçerli
  // (empty/cancelled olmayan) bir atama bulunan rotalar gösterilir.
  const visibleRoutes = useMemo(() => {
    if (!hideEmpty) return depot.routes;
    return depot.routes.filter((r) => {
      for (const d of dates) {
        const a = assignByCell?.get(`${r.id}|${d}`);
        if (a && a.status !== 'empty' && a.status !== 'cancelled') return true;
      }
      return false;
    });
  }, [hideEmpty, depot.routes, dates, assignByCell]);
  const hiddenCount = depot.routes.length - visibleRoutes.length;

  const commit = async () => {
    const next = draft.trim();
    if (!next || next === depot.depot_name) { setEditing(false); setDraft(depot.depot_name || ''); return; }
    const ok = await onRenameDepot?.(depot.depot_id, next);
    if (ok) setEditing(false);
  };
  const cancel = () => { setEditing(false); setDraft(depot.depot_name || ''); };

  return (
    <>
      {depot.depot_name && (
        <tr className="bg-slate-50/50 dark:bg-slate-900/20">
          <td colSpan={8} className="px-6 py-1 text-[11px] font-semibold text-slate-500 sticky left-0">
            <div className="flex items-center gap-1.5">
            {isSuperAdmin && editing ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="opacity-60">↳</span>
                <input
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commit();
                    if (e.key === 'Escape') cancel();
                  }}
                  onBlur={commit}
                  className="text-[11px] font-semibold px-1.5 py-0.5 rounded border border-emerald-300 bg-white dark:bg-slate-800 min-w-[120px]"
                  data-testid={`planning-depot-rename-${depot.depot_id}`}
                />
              </span>
            ) : (
              <button
                onClick={() => isSuperAdmin && onRenameDepot && setEditing(true)}
                disabled={!isSuperAdmin || !onRenameDepot}
                className={`inline-flex items-center gap-1 rounded px-1 -mx-1 ${isSuperAdmin && onRenameDepot ? 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30 hover:text-emerald-700 cursor-pointer' : 'cursor-default'}`}
                title={isSuperAdmin && onRenameDepot ? 'Depo adını değiştir' : undefined}
                data-testid={`planning-depot-name-${depot.depot_id || 'none'}`}
              >
                <span className="opacity-60">↳</span>
                <span>{depot.depot_name}</span>
                {isSuperAdmin && onRenameDepot && (
                  <span className="opacity-0 group-hover:opacity-100 text-[9px] italic text-emerald-600">(düzenle)</span>
                )}
              </button>
            )}
              {/* Boş rotaları gizle/göster toggle. Depoda hiç rota yoksa
                  gösterme (fonksiyonsuz). */}
              {depot.routes.length > 0 && (
                <button
                  type="button"
                  onClick={toggleHideEmpty}
                  className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold border transition-colors ${
                    hideEmpty
                      ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                      : 'border-slate-200 dark:border-slate-700 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                  title={hideEmpty
                    ? `Boş rotaları göster (şu an ${hiddenCount} rota gizli)`
                    : 'Bu haftada atamasız rotaları gizle (ekran alanı kazan)'
                  }
                  data-testid={`planning-depot-toggle-empty-${depot.depot_id || 'none'}`}
                >
                  {hideEmpty ? <ChevronsRight className="h-3 w-3" /> : <ChevronsLeft className="h-3 w-3" />}
                  <span className="hidden sm:inline">
                    {hideEmpty ? `${hiddenCount} gizli` : 'Boşları gizle'}
                  </span>
                </button>
              )}
            </div>
          </td>
        </tr>
      )}
      {visibleRoutes.map((r) => (
        <RouteRow
          key={r.id}
          route={r}
          dates={dates}
          firstColWidth={firstColWidth}
          lockedDates={lockedDates}
          assignByCell={assignByCell}
          onDropCell={onDropCell}
          onDragOverCell={onDragOverCell}
          onDragLeaveCell={onDragLeaveCell}
          dwellingCell={dwellingCell}
          dwellMs={dwellMs}
          onRemoveAssignment={onRemoveAssignment}
          onContextMenu={onContextMenu}
          onAssignmentDragStart={onAssignmentDragStart}
          draggingAssignmentId={draggingAssignmentId}
          onCloseEmptyCell={onCloseEmptyCell}
          onClearRoute={onClearRoute}
          isSuperAdmin={isSuperAdmin}
          focusedEmpId={focusedEmpId}
          liveProgressByCode={liveProgressByCode}
          liveProgressTodayISO={liveProgressTodayISO}
          onLiveChipClick={onLiveChipClick}
          onTapEmptyCell={onTapEmptyCell}
          pickModeActive={pickModeActive}
        />
      ))}
      {hideEmpty && hiddenCount > 0 && (
        <tr className="bg-slate-50/40 dark:bg-slate-900/20">
          <td colSpan={8} className="px-8 py-0.5 text-[10px] italic text-slate-400">
            {hiddenCount} boş rota gizlendi.
            <button
              type="button"
              onClick={toggleHideEmpty}
              className="ml-1 underline text-emerald-600 hover:text-emerald-700"
              data-testid={`planning-depot-show-empty-${depot.depot_id || 'none'}`}
            >
              göster
            </button>
          </td>
        </tr>
      )}
      {depot.routes.length === 0 && depot.depot_name && (
        <tr className="border-b border-dashed border-slate-200 dark:border-slate-700">
          <td colSpan={8} className="px-8 py-1.5 text-[10px] italic text-slate-400">
            Bu depoda (mevcut firma kapsamında) rota yok.
            {isSuperAdmin && ' Depo & Rota dialog\'undan ekleyebilirsin.'}
          </td>
        </tr>
      )}
    </>
  );
}

function RouteRow({ route: r, dates, firstColWidth, lockedDates, assignByCell, onDropCell, onDragOverCell, onDragLeaveCell, dwellingCell, dwellMs, onRemoveAssignment, onContextMenu, onAssignmentDragStart, draggingAssignmentId, onCloseEmptyCell, onClearRoute, isSuperAdmin, focusedEmpId, liveProgressByCode, liveProgressTodayISO, onLiveChipClick, onTapEmptyCell, pickModeActive }) {
  // Live progress lookup — sadece bugün hücresi için anlamlı, AssignmentCell'e
  // o gün varsa geçilir. Route code formatları çeşitli olabilir (3-4 hane,
  // alfanumerik); hook her ride'ı birden fazla key ile map'liyor, biz de
  // birkaç olası variant'ı sırasıyla deniyoruz.
  const liveProgress = (() => {
    if (!liveProgressByCode || !r.route_code) return null;
    const code = String(r.route_code).trim();
    if (!code) return null;
    if (liveProgressByCode.has(code)) return liveProgressByCode.get(code);
    // Numeric ise leading zero ekleyip dene (örn. "510" → "0510")
    if (/^\d+$/.test(code)) {
      const padded = code.padStart(4, '0');
      if (liveProgressByCode.has(padded)) return liveProgressByCode.get(padded);
    }
    return null;
  })();
  return (
    <tr className="border-b border-slate-100 dark:border-slate-800">
      <td
        className="px-2 py-1.5 sticky left-0 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700"
        style={firstColWidth ? { width: firstColWidth, minWidth: firstColWidth, maxWidth: firstColWidth } : undefined}
      >
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-xs font-semibold truncate">{r.route_code}</span>
          {r.owner_firm_name && (
            <span
              className="text-[9px] px-1 py-0 rounded bg-indigo-100 text-indigo-700 font-bold uppercase tracking-tight"
              title={`Rota sahibi: ${r.owner_firm_name}`}
            >
              {r.owner_firm_name}
            </span>
          )}
        </div>
        <div className="text-[10px] text-slate-500 truncate">{r.region_name || '—'} · {r.default_start_time || ''}</div>
        {onClearRoute && (
          <button
            type="button"
            onClick={() => onClearRoute(r)}
            className="mt-0.5 inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-semibold text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors"
            title="Bu rotadaki tüm haftanın (Pzt–Paz) atamalarını temizle"
            data-testid={`planning-route-clear-${r.id}`}
          >
            <Eraser className="h-2.5 w-2.5" /> Haftayı temizle
          </button>
        )}
      </td>
      {dates.map((dateStr) => {
        const a = assignByCell.get(`${r.id}|${dateStr}`);
        const isDwelling = !!dwellingCell && dwellingCell.routeId === r.id && dwellingCell.dateStr === dateStr;
        // Locked-day visual: rose tint + diagonal stripes overlay so the
        // operator instantly knows "1 Mayıs" / firma kilidi cells aren't
        // actionable. The drop handler in Planning.jsx already toasts the
        // reason and short-circuits, but blocking dragOver here also disables
        // Kilitli (resmi tatil / kapatılmış) günler ARTIK atama yapılmasını
        // engellemiyor — operatör isterse (örn. Kurban Bayramı'nda nöbetçi
        // çalışan) atamayı kabul ediyoruz. Sadece görsel olarak kilit dili
        // (rose çizgili pattern) korunur, drop ve drag çalışır.
        const isLocked = !!(lockedDates && lockedDates[dateStr]);
        const tapTarget = !a && pickModeActive && isSuperAdmin && !!onTapEmptyCell;
        return (
          <td
            key={dateStr}
            className={`border-r border-slate-100 dark:border-slate-800 align-top p-1 relative ${isDwelling ? 'bg-emerald-100/60 dark:bg-emerald-900/30' : ''} ${isLocked ? 'bg-rose-50/60 dark:bg-rose-950/20' : ''} ${tapTarget ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30 ring-1 ring-inset ring-blue-200 dark:ring-blue-800' : ''}`}
            onDragOver={(e) => {
              if (!isSuperAdmin) return;
              e.preventDefault();
              if (onDragOverCell) onDragOverCell(e, r, dateStr);
            }}
            onDragLeave={() => isSuperAdmin && onDragLeaveCell && onDragLeaveCell(r, dateStr)}
            onDrop={(e) => isSuperAdmin && onDropCell(e, r, dateStr)}
            onClick={tapTarget ? () => onTapEmptyCell(r, dateStr) : undefined}
            onContextMenu={(e) => {
              if (!isSuperAdmin || !a || !onContextMenu) return;
              e.preventDefault();
              onContextMenu({ x: e.clientX, y: e.clientY, assignment: a, route: r });
            }}
            data-testid={`planning-cell-${r.id}-${dateStr}`}
            title={tapTarget ? 'Buraya dokunarak seçili çalışanı ata' : (isLocked ? `Kilitli: ${lockedDates[dateStr]} — yine de atama yapabilirsin` : undefined)}
          >
            {isLocked && (
              <div
                className="absolute inset-0 pointer-events-none opacity-40"
                style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(244,63,94,.18) 0 4px, transparent 4px 10px)' }}
                data-testid={`planning-cell-locked-${r.id}-${dateStr}`}
              />
            )}
            {isDwelling && (
              // Dwell indicator kilitli hücrelerde de artık gösteriliyor —
              // kullanıcı isteği üzerine kilitli günlere de dwell-to-fill ile
              // atama yapabiliyor.
              <div
                className="absolute inset-0 pointer-events-none border-2 border-emerald-400 rounded animate-pulse"
                style={{ animationDuration: `${dwellMs || 700}ms` }}
                data-testid={`planning-dwell-${r.id}-${dateStr}`}
              />
            )}
            {a
              ? <AssignmentCell a={a} route={r} isSuperAdmin={isSuperAdmin} onRemove={onRemoveAssignment} onDragStart={onAssignmentDragStart} isGhost={draggingAssignmentId === a.id} isFocused={!!focusedEmpId && a.employee_id === focusedEmpId} liveProgress={dateStr === liveProgressTodayISO ? liveProgress : null} onLiveChipClick={onLiveChipClick} />
              : <EmptyCell isSuperAdmin={isSuperAdmin} onClose={onCloseEmptyCell ? () => onCloseEmptyCell(r, dateStr) : null} />}
          </td>
        );
      })}
    </tr>
  );
}

function AssignmentCell({ a, route, isSuperAdmin, onRemove, onDragStart, isGhost, isFocused, liveProgress, onLiveChipClick }) {
  // "Closed" placeholder — created when operator marks a (route, day) as
  // "no work" via the Empty cell close-button. No employee, status='empty'.
  // Rendered compact and visually distinct from a real assignment.
  if (a.status === 'empty' && !a.employee_id) {
    return (
      <div
        className={`group rounded border border-dashed ${STATUS_STYLES.empty} h-9 flex items-center justify-center gap-1 text-[10px] text-slate-500`}
        data-testid={`planning-closed-${a.id}`}
        title="Bu gün için iş yok (kapalı)"
      >
        <MinusCircle className="h-3 w-3 text-slate-400" />
        <span className="font-medium">Kapalı</span>
        {isSuperAdmin && (
          <button
            onClick={() => onRemove(a)}
            className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 text-slate-400 hover:text-emerald-600"
            title="Kapatmayı geri al (yeniden aç)"
            data-testid={`planning-reopen-${a.id}`}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }
  return (
    <div
      draggable={isSuperAdmin && !!onDragStart}
      onDragStart={(e) => onDragStart && onDragStart(e, a)}
      className={`group rounded border px-2 py-1 text-xs ${
        a.cell_color
          ? ''
          : (a.is_subcontractor
              ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700/60 text-amber-900 dark:text-amber-100'
              : (STATUS_STYLES[a.status] || STATUS_STYLES.assigned))
      } ${isSuperAdmin && onDragStart ? 'cursor-grab active:cursor-grabbing' : ''} ${isGhost ? 'opacity-30 ring-2 ring-dashed ring-amber-400 transition-opacity' : 'transition-opacity'} ${isFocused ? 'ring-2 ring-rose-500 ring-offset-1 shadow-[0_0_0_4px_rgba(244,63,94,0.18)] !bg-rose-100 dark:!bg-rose-900/40 !border-rose-400' : ''}`}
      style={a.cell_color ? {
        // Şub 2026 — Kullanıcı tarafından atanan özel hücre rengi; taşeron
        // veya default statü stilini ezer. Yalnızca arka plan (alfa 20%) ve
        // border ile renk vurgulanır; metin okunabilirlik için karartılmaz.
        backgroundColor: `${a.cell_color}22`,
        borderColor: a.cell_color,
      } : undefined}
      data-testid={`planning-assignment-${a.id}`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="font-medium truncate">{a.employee_name || '—'}</span>
        {isSuperAdmin && (
          <button
            onClick={() => onRemove(a)}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500"
            title="Atamayı kaldır"
            data-testid={`planning-remove-${a.id}`}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {a.is_subcontractor ? (
          <span
            className="text-[9px] px-1 py-0 rounded bg-amber-200 dark:bg-amber-700/60 text-amber-900 dark:text-amber-100 font-bold uppercase tracking-tight border border-amber-400 dark:border-amber-600"
            title="Taşeron sürücü kaydı (manuel) — AYTU/HYK/FAST-TRANS dışı"
            data-testid={`planning-subcontractor-tag-${a.id}`}
          >
            Taşeron
          </span>
        ) : (
          a.firm_name && (
            <span className="text-[9px] px-1 py-0 rounded bg-indigo-100 text-indigo-700 font-semibold uppercase tracking-tight">
              {a.firm_name}
            </span>
          )
        )}
        {!a.is_subcontractor && route.owner_firm_id && a.firm_id && route.owner_firm_id !== a.firm_id && (
          <span
            className="text-[9px] px-1 py-0 rounded bg-amber-100 text-amber-800 font-bold uppercase tracking-tight border border-amber-300"
            title={`Bu çalışan ${a.firm_name} firmasından, rota ${route.owner_firm_name} firmasına ait`}
            data-testid={`planning-borrowed-${a.id}`}
          >
            Ödünç
          </span>
        )}
        {a.vehicle_code && (
          <span
            className="text-[9px] px-1 py-0 rounded bg-sky-100 text-sky-800 font-bold uppercase tracking-tight border border-sky-300 font-mono"
            title={`Plaka: ${a.vehicle_code}`}
            data-testid={`planning-plate-${a.id}`}
          >
            {a.vehicle_code}
          </span>
        )}
        {a.shift_name && <span className="text-[10px] opacity-70">{a.shift_name}</span>}
      </div>
      {a.region_name && <div className="text-[10px] opacity-60 truncate">{a.region_name}</div>}
      {liveProgress && <LiveProgressChip lp={liveProgress} onClick={onLiveChipClick} />}
    </div>
  );
}

function LiveProgressChip({ lp, onClick }) {
  // Bugün hücresinde canlı ride durumu — Ritmonitor'dan 5dk'da bir güncelleniyor.
  // Renk paleti: tamamlandı (emerald), %60+ (orange), <%60 (slate).
  const pct = lp.donePct || 0;
  const tone = lp.remaining === 0 && lp.stops > 0
    ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-700/60'
    : pct >= 60
      ? 'border-orange-300 bg-orange-50 text-orange-800 dark:bg-orange-900/30 dark:border-orange-700/60'
      : 'border-slate-300 bg-slate-50 text-slate-700 dark:bg-slate-800 dark:border-slate-600';
  const barTone = lp.remaining === 0 && lp.stops > 0
    ? 'bg-emerald-500'
    : pct >= 60 ? 'bg-orange-500' : 'bg-slate-400';
  const clickable = !!onClick && !!lp.ritnaam;
  const handleClick = clickable
    ? (e) => { e.stopPropagation(); onClick(lp.ritnaam, lp); }
    : undefined;
  return (
    <div
      onClick={handleClick}
      onPointerDown={clickable ? (e) => e.stopPropagation() : undefined}
      className={`mt-1 rounded border ${tone} px-1.5 py-0.5 flex items-center gap-1.5 ${clickable ? 'cursor-pointer hover:ring-2 hover:ring-orange-300 transition' : ''}`}
      title={`Canlı: ${lp.done}/${lp.stops} stop tamamlandı • Kalan ${lp.remaining} • Son aksiyon ${lp.lastAction || '—'}${clickable ? ' (tıkla: detay)' : ''}`}
      data-testid={`planning-live-progress`}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
      <span className="text-[9px] font-bold tabular-nums leading-none">
        {lp.done}/{lp.stops}
      </span>
      <div className="flex-1 h-1 bg-white/60 dark:bg-slate-900/40 rounded overflow-hidden min-w-[20px]">
        <div className={`h-full ${barTone} transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[9px] font-mono opacity-70 shrink-0">{lp.lastAction || ''}</span>
    </div>
  );
}

function EmptyCell({ isSuperAdmin, onClose }) {
  return (
    <div className={`group relative rounded border ${STATUS_STYLES.empty} h-9 flex items-center justify-center text-[10px] text-slate-400`}>
      <span>{isSuperAdmin ? 'Sürükle' : '—'}</span>
      {isSuperAdmin && onClose && (
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          title="Bu günü kapat (iş yok)"
          data-testid="planning-close-empty"
        >
          <MinusCircle className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
